import { useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Eyebrow } from "@/components/ui-custom/Eyebrow";
import { Reveal } from "@/components/ui-custom/Reveal";
import { TrustBadges } from "@/components/ui-custom/TrustBadges";
import { QuoteForm } from "@/components/ui-custom/QuoteForm";
import { NJServiceMap } from "@/components/ui-custom/NJServiceMap";
import { SITE, TRUST_BADGES } from "@/data/site";
import { useSEO, localBusinessSchema } from "@/lib/seo";

const InfoRow = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-4">
    <span className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: 8, border: "1px solid var(--border-dark)", color: "var(--gold)" }}>
      <Icon size={20} strokeWidth={1.5} />
    </span>
    <div className="min-w-0">
      <div className="uppercase" style={{ color: "rgba(245,242,236,0.5)", fontSize: "12px", letterSpacing: "0.16em", marginBottom: 3 }}>{label}</div>
      <div data-testid={testId} className="break-words" style={{ color: "var(--text-light)", fontSize: "17px", fontWeight: 500 }}>{children}</div>
    </div>
  </div>
);

export default function Contact() {
  useSEO({
    title: "Contact Us | Free Estimate | AIM High",
    description:
      "Request a free estimate from AIM High in Colts Neck, NJ. Call (908) 415-7371 or send project details — residential & commercial work.",
    path: "/contact",
    jsonLd: [localBusinessSchema()],
  });

  useEffect(() => {
    if (window.location.hash !== "#quote") return;
    const t = setTimeout(() => {
      const el = document.getElementById("quote");
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <section className="relative overflow-hidden" style={{ background: "var(--black)" }} data-testid="contact-hero">
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 80% at 80% 0%, rgba(201,162,75,0.1) 0%, rgba(0,0,0,0) 55%)" }} />
        <div className="container-1200 relative z-10" style={{ paddingTop: "72px", paddingBottom: "32px" }}>
          <Reveal><Eyebrow start>Free Estimate</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="h1 mt-6" style={{ color: "var(--text-light)", maxWidth: "18ch" }} data-testid="contact-h1">Get Your Free Estimate</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="lead mt-5" style={{ color: "rgba(245,242,236,0.78)", maxWidth: "54ch" }}>
              Tell us about your project and we'll get right back to you. Honest pricing, no surprises, and no pressure — that's a promise.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="sec-black" style={{ paddingBottom: "96px" }} data-testid="contact-body">
        <div className="container-1200 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Left: contact card + map */}
          <div>
            <div style={{ background: "var(--charcoal)", border: "1px solid var(--border-dark)", borderRadius: 8, padding: "32px" }} data-testid="contact-card">
              <div className="space-y-6">
                <InfoRow icon={Phone} label="Call us" testId="contact-phone">
                  <a href={SITE.phoneHref} style={{ color: "var(--text-light)" }}>{SITE.phone}</a>
                </InfoRow>
                <InfoRow icon={Mail} label="Email" testId="contact-email">
                  <a href={`mailto:${SITE.email}`} className="break-all">{SITE.email}</a>
                </InfoRow>
                <InfoRow icon={MapPin} label="Address" testId="contact-address">
                  <a href={SITE.mapsLink} target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-light)" }}>{SITE.address.full}</a>
                </InfoRow>
                <InfoRow icon={Clock} label="Hours" testId="contact-hours">
                  {SITE.hours}
                </InfoRow>
              </div>
              <div className="divider-gold my-8" />
              <TrustBadges items={TRUST_BADGES} onDark />
            </div>

            <a
              href={SITE.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="mt-6 block relative overflow-hidden group"
              style={{ borderRadius: 8, border: "1px solid var(--border-dark)" }}
            >
              <iframe
                title="AIM High — 16 New Street, Colts Neck, NJ"
                src={SITE.mapEmbed}
                width="100%"
                height="320"
                style={{ border: 0, display: "block", pointerEvents: "none" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map-iframe"
              />
              <span
                className="absolute flex items-center gap-2"
                style={{ right: 14, bottom: 14, background: "var(--black)", border: "1px solid var(--cta)", color: "var(--cta)", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 600, boxShadow: "0 6px 18px rgba(0,0,0,0.35)" }}
              >
                <MapPin size={15} strokeWidth={2} /> Open in Google Maps
              </span>
            </a>
          </div>

          {/* Right: form */}
          <div id="quote" style={{ background: "var(--white)", borderRadius: 8, padding: "32px" }} data-testid="contact-form-wrap">
            <h2 className="h3" style={{ color: "var(--text-dark)" }}>Request a Quote</h2>
            <p className="mt-2 mb-6 text-muted-warm" style={{ fontSize: "15px" }}>
              Fields marked * are required.
            </p>
            <QuoteForm variant="full" source="contact_page" />
          </div>
        </div>
      </section>

      {/* NJ service-area map */}
      <NJServiceMap />
    </>
  );
}
